import React, { useState, useEffect } from 'react';
import { Box, CardMedia, CircularProgress } from '@mui/material';
import { keyframes } from '@mui/system';
import { ParsedSlide } from '../../types/base';

// Simple continuous pulse animation for hovered elements
const hoverPulse = keyframes`
  0% { 
    transform: scale(1);
    box-shadow: 0 0 0 0 currentColor;
  }
  50% { 
    transform: scale(1.02);
    box-shadow: 0 0 20px 2px currentColor;
  }
  100% { 
    transform: scale(1);
    box-shadow: 0 0 0 0 currentColor;
  }
`;

interface SlidePreviewWithBoundariesProps {
  fileId: string;
  slide: ParsedSlide;
  slideNumber: number;
  isLoading?: boolean;
  hoveredElementId?: string;
  slideWidth?: number; // Width in EMUs
  slideHeight?: number; // Height in EMUs
  onElementHover?: (elementIndex: number | null) => void;
  onClick?: (elementIndices: number[], defaultIndex: number | null, event: React.MouseEvent) => void;
  isElementLocked?: (elementIndex: number) => boolean;
}

function SlidePreviewWithBoundaries({ 
  fileId, 
  slide, 
  slideNumber,
  isLoading, 
  hoveredElementId,
  slideWidth = 12192000, // Default to widescreen 16:9 (13.333 inches)
  slideHeight = 6858000, // Default to widescreen 16:9 (7.5 inches)
  onElementHover,
  onClick,
  isElementLocked
}: SlidePreviewWithBoundariesProps) {
  // Local hover state (GLOBAL element index)
  const [localHoveredIndex, setLocalHoveredIndex] = useState<number | null>(null);

  // Convert external hovered id (string | number) to global index number
  const externalHoveredIndex: number | null = React.useMemo(() => {
    if (hoveredElementId === undefined || hoveredElementId === null) return null;
    const parsed = Number(hoveredElementId);
    return Number.isFinite(parsed) ? parsed : null;
  }, [hoveredElementId]);

  // Convert EMU to percentage of the container based on provided slide dimensions
  const convertEmuToPercent = (emu: number, isWidth: boolean) => {
    const maxEmu = isWidth ? slideWidth : slideHeight;
    return (emu / maxEmu) * 100;
  };

  const getElementColors = (type: string, isHovered: boolean, isLocked: boolean) => {
    // Stronger alpha difference for better visibility
    const alpha = isHovered ? '0.5' : '0.1';
    
    // If element is locked, return locked colors
    if (isLocked) {
      return {
        border: isHovered ? '#FF3D00' : '#FF7043', // Brighter red when hovered
        background: `rgba(255, 61, 0, ${alpha})`,
        shadow: isHovered ? 'rgba(255, 61, 0, 0.6)' : 'transparent'
      };
    }
    
    switch (type) {
      case 'shape':
        return {
          border: isHovered ? '#1976D2' : '#42A5F5', // Darker blue when hovered
          background: `rgba(25, 118, 210, ${alpha})`,
          shadow: isHovered ? 'rgba(25, 118, 210, 0.6)' : 'transparent'
        };
      case 'picture':
        return {
          border: isHovered ? '#388E3C' : '#66BB6A', // Darker green when hovered
          background: `rgba(56, 142, 60, ${alpha})`,
          shadow: isHovered ? 'rgba(56, 142, 60, 0.6)' : 'transparent'
        };
      case 'table':
        return {
          border: isHovered ? '#F57C00' : '#FFA726', // Darker orange when hovered
          background: `rgba(245, 124, 0, ${alpha})`,
          shadow: isHovered ? 'rgba(245, 124, 0, 0.6)' : 'transparent'
        };
      case 'chart':
        return {
          border: isHovered ? '#C2185B' : '#EC407A', // Darker pink when hovered
          background: `rgba(194, 24, 91, ${alpha})`,
          shadow: isHovered ? 'rgba(194, 24, 91, 0.6)' : 'transparent'
        };
      default:
        return {
          border: isHovered ? '#7B1FA2' : '#AB47BC', // Darker purple when hovered
          background: `rgba(123, 31, 162, ${alpha})`,
          shadow: isHovered ? 'rgba(123, 31, 162, 0.6)' : 'transparent'
        };
    }
  };

  // Get visible elements - we need to check for the property on each element safely
  const visibleElements = slide.elements.filter(element => {
    // @ts-ignore - handle the hidden property added in PptxFileDetailPage
    return element.hidden !== true;
  });

  const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!onClick) return;

    // Get the container element
    const container = event.currentTarget;
    const rect = container.getBoundingClientRect();
    
    // Calculate click position relative to container
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    
    // Convert to percentage (0-100)
    const clickXPercent = (clickX / rect.width) * 100;
    const clickYPercent = (clickY / rect.height) * 100;
    
    // Find all elements that contain the click point
    const matchingElements: { index: number; area: number }[] = [];
    
    visibleElements.forEach((element, index) => {
      if (!element.boundary) return;
      
      // Convert boundary to percentage
      const elementLeft = convertEmuToPercent(element.boundary.x * 914400, true);
      const elementTop = convertEmuToPercent(element.boundary.y * 914400, false);
      const elementWidth = convertEmuToPercent(element.boundary.width * 914400, true);
      const elementHeight = convertEmuToPercent(element.boundary.height * 914400, false);
      
      // Check if click is within element bounds
      if (
        clickXPercent >= elementLeft &&
        clickXPercent <= elementLeft + elementWidth &&
        clickYPercent >= elementTop &&
        clickYPercent <= elementTop + elementHeight
      ) {
        // Calculate area for determining the smallest element
        const area = elementWidth * elementHeight;
        matchingElements.push({ index, area });
      }
    });
    
    if (matchingElements.length > 0) {
      // Sort by area to find smallest
      matchingElements.sort((a, b) => a.area - b.area);
      
      // Extract indices
      const elementIndices = matchingElements.map(el => el.index);
      const defaultIndex = matchingElements[0].index; // Smallest element
      
      onClick(elementIndices, defaultIndex, event);
    } else {
      // No elements at this position
      onClick([], null, event);
    }
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    // Get the container element
    const container = event.currentTarget;
    const rect = container.getBoundingClientRect();
    
    // Calculate mouse position relative to container
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    
    // Convert to percentage (0-100)
    const mouseXPercent = (mouseX / rect.width) * 100;
    const mouseYPercent = (mouseY / rect.height) * 100;
    
    // We store GLOBAL element index here to keep consistency with external ids
    let smallestElement: { globalIndex: number; area: number } | null = null;
    
    visibleElements.forEach((element) => {
      const globalIndex = slide.elements.indexOf(element);
      if (globalIndex === -1) return;
      
      if (!element.boundary) return;
      
      // Convert boundary to percentage
      const elementLeft = convertEmuToPercent(element.boundary.x * 914400, true);
      const elementTop = convertEmuToPercent(element.boundary.y * 914400, false);
      const elementWidth = convertEmuToPercent(element.boundary.width * 914400, true);
      const elementHeight = convertEmuToPercent(element.boundary.height * 914400, false);
      
      // Check if mouse is within element bounds
      if (
        mouseXPercent >= elementLeft &&
        mouseXPercent <= elementLeft + elementWidth &&
        mouseYPercent >= elementTop &&
        mouseYPercent <= elementTop + elementHeight
      ) {
        const area = elementWidth * elementHeight;
        if (!smallestElement || area < smallestElement.area) {
          smallestElement = { globalIndex, area };
        }
      }
    });
    
    // Update local hover state
    const newHoveredIndex = smallestElement ? (smallestElement as { globalIndex: number }).globalIndex : null;
    if (newHoveredIndex !== localHoveredIndex) {
      setLocalHoveredIndex(newHoveredIndex);
      // Also call parent's onElementHover if provided
      if (onElementHover) {
        onElementHover(newHoveredIndex);
      }
    }
  };

  const handleMouseLeave = () => {
    setLocalHoveredIndex(null);
    if (onElementHover) {
      onElementHover(null);
    }
  };

  // Determine if the currently hovered element (external or local) is locked
  const effectiveHoveredIndex = externalHoveredIndex !== null ? externalHoveredIndex : localHoveredIndex;
  const isHoveredElementLocked = effectiveHoveredIndex !== null && isElementLocked
    ? isElementLocked(effectiveHoveredIndex)
    : false;

  return (
    <Box 
      sx={{ 
        position: 'relative', 
        width: '100%', 
        paddingTop: '56.25%', /* 16:9 aspect ratio */
        cursor: isHoveredElementLocked ? 'not-allowed' : 'pointer'
      }}
      onClick={handleContainerClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <CardMedia
        component="img"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          bgcolor: 'grey.100',
          border: theme => `1px solid ${theme.palette.divider}`,
        }}
        image={`/api/files/thumbnails/${fileId}/${slideNumber}.jpg`}
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          const img = e.currentTarget;
          if (!img.getAttribute('data-error')) {
            img.setAttribute('data-error', 'true');
            img.src = '/images/placeholder-slide.jpg';
          }
        }}
        alt={`Slide ${slideNumber}`}
      />
      
      {/* Element boundary overlays - only show for visible elements */}
      {visibleElements.map((element, visibleIndex) => {
        // Global index of this element in the slide.elements array
        const globalIndex = slide.elements.indexOf(element);

        // Determine the effective hovered index prioritising external signal
        const isHovered = externalHoveredIndex !== null
          ? externalHoveredIndex === globalIndex
          : localHoveredIndex === globalIndex;

        const anyHovered = externalHoveredIndex !== null || localHoveredIndex !== null;
        // Determine effective lock status: consult parent callback if provided, otherwise fall back to element flag
        const isLocked = isElementLocked ? isElementLocked(globalIndex) : (element as any).isLocked === true;
        // @ts-ignore - handle the valuePresent property added in SlidesEditor
        const hasValue = element.valuePresent === true;
        const colors = getElementColors(element.type, isHovered, isLocked);
        
        return element.boundary && (
          <Box
            key={visibleIndex}
            sx={{
              position: 'absolute',
              left: `${convertEmuToPercent(element.boundary.x * 914400, true)}%`,
              top: `${convertEmuToPercent(element.boundary.y * 914400, false)}%`,
              width: `${convertEmuToPercent(element.boundary.width * 914400, true)}%`,
              height: `${convertEmuToPercent(element.boundary.height * 914400, false)}%`,
              border: isHovered ? '4px solid' : '2px solid',
              borderColor: colors.border,
              borderStyle: isLocked ? 'dotted' : 'solid',
              backgroundColor: colors.background,
              pointerEvents: 'none',
              transition: 'all 0.15s ease-in-out',
              zIndex: isHovered ? 10 : 1,
              opacity: anyHovered && !isHovered ? 0.5 : 1,
              filter: anyHovered && !isHovered ? 'brightness(0.7)' : 'none',
              // Apply continuous pulse animation when hovered
              animation: isHovered ? `${hoverPulse} 1.5s ease-in-out infinite` : 'none',
              boxShadow: isHovered ? `0 0 25px 3px ${colors.shadow}` : 'none',
              // X overlay for elements with values
              '&::before': hasValue ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(to bottom right, transparent calc(50% - 1px), ${colors.border} calc(50% - 1px), ${colors.border} calc(50% + 1px), transparent calc(50% + 1px))`,
                pointerEvents: 'none',
              } : {},
              '&::after': hasValue ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(to top right, transparent calc(50% - 1px), ${colors.border} calc(50% - 1px), ${colors.border} calc(50% + 1px), transparent calc(50% + 1px))`,
                pointerEvents: 'none',
              } : {},
            }}
          />
        );
      })}

      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}

export default SlidePreviewWithBoundaries; 